<template>
<div class="chapter">
<div class="inner_page">
<h2 class="fm-title"><a id="page17">Using the Yearbook of Astronomy as an Observing Guide</a></h2><a id="page17">
<h3 class="ch-sub1">Notes on the Monthly Star Charts</h3>
<p class="noindent">The star charts on the following pages show the night sky throughout the year. There are two sets of charts, one for use by observers in the Northern Hemisphere and one for those in the Southern Hemisphere. The first set is drawn for latitude 52°N and can be used by observers in Europe, Canada and most of the United States. The second set is drawn for latitude 35°S and show the stars as seen from Australia and New Zealand. Twelve pairs of charts are provided for each of these latitudes.</p>
<p class="indent">Each pair of charts shows the entire sky as two semi-circular half-sky views, one looking north and the other looking south. A given pair of charts can be used at different times of year. For example, chart 1 shows the night sky at midnight on 21 December, but also at 2am on 21 January, 4am on 21 February and so forth. The accompanying table will enable you to select the correct chart for a given month and time of night. The caption next to each chart also lists the dates and times of night for which it is valid.</p>
<p class="indent">The charts are intended to help you find the more prominent constellations and other objects of interest mentioned in the monthly observing notes. To avoid the charts becoming too crowded, only stars of magnitude 4.5 or brighter are shown. This corresponds to stars that are bright enough to be seen from any dark suburban garden on a night when the Moon is not too close to full phase.</p>
<p class="indent">Each constellation is depicted by joining selected stars with lines to form a pattern. There is no official standard for these patterns, so you may occasionally find different patterns used in other popular astronomy books for some of the constellations.</p>
<p class="indent">Any map projection from a sphere onto a flat page will by necessity contain some distortions. This is true of star charts as well as maps of the Earth. The distortion on the half-sky charts is greatest near the semi-circular boundary of each chart, where it may appear to stretch constellation patterns out of shape.</p>
<p class="indent">The charts also show selected deep-sky objects such as galaxies, nebulae and star clusters. Many of these objects are too faint to be seen with the naked eye, and you will need binoculars or a telescope to observe them. Please refer to the table of deep-sky objects for more information.</p>
</a><h3 class="ch-sub1"><a id="page17"></a><a id="page18">Planetary Apparition Diagrams</a></h3><a id="page18">
<p class="noindent">The diagrams of the apparitions of Mercury and Venus show the position of the respective planet in the sky at the moment of sunrise or sunset throughout the entire apparition. Two sets of positions are plotted on each chart: for latitude 52° North (blue line) and for latitude 35° South (red line). A thin dotted line denotes the portion of the apparition which falls outside the year covered by this edition of the Yearbook. A white dot indicates the position of Venus on the first day of each month, or of Mercury on the first, eleventh and 21st of the month. The day of greatest elongation (GE) is also marked by a white dot. Note that the dots do NOT indicate the magnitude of the planet.</p>
<p class="indent">There are two finder charts for Mars, which comes to opposition on 8 December. The first chart, in the January notes, shows its path during the first half of 2022, when it is a morning object as it moves away from the Sun. Mars traverses 100° in ecliptic longitude during this period, moving from Ophiuchus in January to the Pisces/Aries border in late June, so the chart is based upon the ecliptic, which runs across the centre of the chart from right to left. The position of Mars is indicated on the 1st of each month from January to July. The second chart, in the December notes, uses a conventional R.A./Dec. grid and follows Mars from 1 July 2022 to 1 February 2023 as it crosses Aries and performs its retrograde loop among the stars of Taurus. On this chart, the position of Mars is indicated on the 1st of each month, and also at its stationary points and on the date of opposition. Stars are shown to magnitude 5.5 on both charts. Note that the sizes of the Mars dots do NOT indicate its magnitude.</p>
<p class="indent">The finder charts for Jupiter, Saturn, Uranus and Neptune show the paths of the planets throughout the year. The position of each planet is indicated at opposition and at stationary points, as well as the start and end of the year and on the 1st of each month (1st of April, July and October only for Uranus and Neptune) where these dates do not fall too close to an event that is already marked. Stars are shown to magnitude 5.5 on the charts for Jupiter and Saturn. On the Uranus chart, stars are shown to magnitude 8; on the Neptune chart, the limiting magnitude is 10. In both cases, this is approximately two magnitudes fainter than the planet itself. Right Ascension and Declination scales are shown for the epoch J2000 to allow comparison with modern star charts. Note that the sizes of the dots denoting the planets do NOT indicate their magnitudes.</p>
</a><h3 class="ch-sub1"><a id="page18"></a><a id="page19">Selecting the Correct Charts</a></h3><a id="page19">
<p class="noindent">The table below shows which of the charts to use for particular dates and times throughout the year and will help you to select the correct pair of half-sky charts for any combination of month and time of night.</p>
<p class="indent">The Earth takes 23 hours 56 minutes (and 4 seconds) to rotate once around its axis with respect to the fixed stars. Because this is around four minutes shorter than a full 24 hours, the stars appear to rise and set about 4 minutes earlier on each successive day, or around an hour earlier each fortnight. Therefore, as well as showing the stars at 10pm (22h in 24-hour notation) on 21 January, chart 1 also depicts the sky at 9pm (21h) on 6 February, 8pm (20h) on 21 February and 7pm (19h) on 6 March.</p>
<p class="indent">The times listed do not include summer time (daylight saving time), so if summer time is in force you must subtract one hour to obtain standard time (GMT if you are in the United Kingdom) before referring to the chart. For example, to find the correct chart for mid-September in the northern hemisphere at 3am summer time, first of all subtract one hour to obtain 2am (2h) standard time. Then you can consult the table, where you will find that you should use chart 11.</p>
<p class="indent">The table does not indicate sunrise, sunset or twilight. In northern temperate latitudes, the sky is still light at 18h and 6h from April to September, and still light at 20h and 4h from May to August. In Australia and New Zealand, the sky is still light at 18h and 6h from October to March, and in twilight (with only bright stars visible) at 20h and 04h from November to January.</p>
<div class="img_container1a">
<img alt="image" src="images/pg19_a.jpg"/>
</div>
</a><div class="img_container1a"><a id="page19"></a><a id="page20">
<img alt="image" src="images/pg20_a.jpg"/>
</a></div><a id="page20">
<h3 class="ch-sub1">Star Names</h3>
<p class="noindent">There are over 200 stars with proper names, most of which are of Roman, Greek or Arabic origin although only a couple of dozen or so of these names are used regularly. Examples include Arcturus in Boötes, Castor and Pollux in Gemini and Rigel in Orion.</p>
<p class="indent">A system whereby Greek letters were assigned to stars was introduced by the German astronomer and celestial cartographer Johann Bayer in his star atlas Uranometria, published in 1603. Bayer’s system is applied to the brighter stars within any particular constellation, which are given a letter from the Greek alphabet followed by the genitive case of the constellation in which the star is located. This genitive case is simply the Latin form meaning ‘of’ the constellation. Examples are the stars Alpha Boötis and Beta Centauri which translate literally as ‘Alpha of Boötes’ and ‘Beta of the Centaur’.</p>
<p class="indent">As a general rule, the brightest star in a constellation is labelled Alpha (α), the second brightest Beta (β), and the third brightest Gamma (γ) and so on, although there are some constellations where the system falls down. An example is Gemini where the principal star (Pollux) is designated Beta Geminorum, the second brightest (Castor) being known as Alpha Geminorum.</p>
</a><p class="indent"><a id="page20">There are only 24 letters in the Greek alphabet, the consequence of which was that the fainter naked eye stars needed an alternative system of classification. The system in popular use is that devised by the first Astronomer Royal John Flamsteed in which the stars in each constellation are listed numerically in order from west to </a><a id="page21">east. Although many of the brighter stars within any particular constellation will have both Greek letters and Flamsteed numbers, the latter are generally used only when a star does not have a Greek letter.</a></p><a id="page21">
<h3 class="ch-sub1">The Greek Alphabet</h3>
<div class="img_container1a">
<img alt="image" src="images/pg21_a.jpg"/>
</div>
<h3 class="ch-sub1">The Names of the Constellations</h3>
<p class="noindent">On clear, dark, moonless nights, the sky seems to teem with stars although in reality you can never see more than a couple of thousand or so at any one time when looking with the unaided eye. Each and every one of these stars belongs to a particular constellation, although the constellations that we see in the sky, and which grace the pages of star atlases, are nothing more than chance alignments. The stars that make up the constellations are often situated at vastly differing distances from us and only appear close to each other, and form the patterns that we see, because they lie in more or less the same direction as each other as seen from Earth.</p>
<p class="indent">A large number of the constellations are named after mythological characters, and were given their names thousands of years ago. However, those star groups lying close to the south celestial pole were discovered by Europeans only during the last few centuries, many of these by explorers and astronomers who mapped the stars during their journeys to lands under southern skies. This resulted in many of the newer constellations having modern-sounding names, such as Octans (the Octant) and Microscopium (the Microscope), both of which were devised by the French astronomer Nicolas Louis De La Caille during the early 1750s.</p>
</a><p class="indent"><a id="page21">Over the centuries, many different suggestions for new constellations have been put forward by astronomers who, for one reason or another, felt the need to add new groupings to star charts and to fill gaps between the traditional constellations. Astronomers drew up their own charts of the sky, incorporating their new groups </a><a id="page22">into them. A number of these new constellations had cumbersome names, notable examples including Officina Typographica (the Printing Shop) introduced by the German astronomer Johann Bode in 1801; Sceptrum Brandenburgicum (the Sceptre of Brandenburg) introduced by the German astronomer Gottfried Kirch in 1688; Taurus Poniatovii (Poniatowski’s Bull) introduced by the Polish-Lithuanian astronomer Martin Odlanicky Poczobut in 1777; and Quadrans Muralis (the Mural Quadrant) devised by the French astronomer Joseph-Jerôme de Lalande in1795. Although these have long since been rejected, the latter has been immortalised by the annual Quadrantid meteor shower, the radiant of which lies in an area of sky formerly occupied by Quadrans Muralis.</a></p><a id="page22">
<p class="indent">During the 1920s the International Astronomical Union (IAU) systemised matters by adopting an official list of 88 accepted constellations, each with official spellings and abbreviations. Precise boundaries for each constellation were then drawn up so that every point in the sky belonged to a particular constellation.</p>
<p class="indent">The abbreviations devised by the IAU each have three letters which in the majority of cases are the first three letters of the constellation name, such as AND for Andromeda, EQU for Equuleus, HER for Hercules, ORI for Orion and so on. This trend is not strictly adhered to in cases where confusion may arise. This happens with the two constellations Leo (abbreviated LEO) and Leo Minor (abbreviated LMI). Similarly, because Triangulum (TRI) may be mistaken for Triangulum Australe, the latter is abbreviated TRA. Other instances occur with Sagitta (SGE) and Sagittarius (SGR) and with Canis Major (CMA) and Canis Minor (CMI) where the first two letters from the second names of the constellations are used. This is also the case with Corona Australis (CRA) and Corona Borealis (CRB) where the first letter of the second name of each constellation is incorporated. Finally, mention must be made of Crater (CRT) which has been abbreviated in such a way as to avoid confusion with the aforementioned CRA (Corona Australis)..</p>
<p class="indent">The table shown on the following pages contains the name of each of the 88 constellations together with the translation and abbreviation of the constellation name. The constellations depicted on the monthly star charts are identified with their abbreviations rather than the full constellation names.</p>
</a><h3 class="ch-sub1"><a id="page22"></a><a id="page23">The Constellations</a></h3><a id="page23">
<table class="tabq">
<tbody><tr>
<td class="tab-D">Andromeda</td>
<td class="tab-D">Andromeda</td>
<td class="tab-D">AND</td>
</tr>
<tr>
<td class="tab-D">Antlia</td>
<td class="tab-D">The Air Pump</td>
<td class="tab-D">ANT</td>
</tr>
<tr>
<td class="tab-D">Apus</td>
<td class="tab-D">The Bird of Paradise</td>
<td class="tab-D">APS</td>
</tr>
<tr>
<td class="tab-D">Aquarius</td>
<td class="tab-D">The Water Carrier</td>
<td class="tab-D">AQR</td>
</tr>
<tr>
<td class="tab-D">Aquila</td>
<td class="tab-D">The Eagle</td>
<td class="tab-D">AQL</td>
</tr>
<tr>
<td class="tab-D">Ara</td>
<td class="tab-D">The Altar</td>
<td class="tab-D">ARA</td>
</tr>
<tr>
<td class="tab-D">Aries</td>
<td class="tab-D">The Ram</td>
<td class="tab-D">ARI</td>
</tr>
<tr>
<td class="tab-D">Auriga</td>
<td class="tab-D">The Charioteer</td>
<td class="tab-D">AUR</td>
</tr>
<tr>
<td class="tab-D">Boötes</td>
<td class="tab-D">The Herdsman</td>
<td class="tab-D">BOO</td>
</tr>
<tr>
<td class="tab-D">Caelum</td>
<td class="tab-D">The Graving Tool</td>
<td class="tab-D">CAE</td>
</tr>
<tr>
<td class="tab-D">Camelopardalis</td>
<td class="tab-D">The Giraffe</td>
<td class="tab-D">CAM</td>
</tr>
<tr>
<td class="tab-D">Cancer</td>
<td class="tab-D">The Crab</td>
<td class="tab-D">CNC</td>
</tr>
<tr>
<td class="tab-D">Canes Venatici</td>
<td class="tab-D">The Hunting Dogs</td>
<td class="tab-D">CVN</td>
</tr>
<tr>
<td class="tab-D">Canis Major</td>
<td class="tab-D">The Great Dog</td>
<td class="tab-D">CMA</td>
</tr>
<tr>
<td class="tab-D">Canis Minor</td>
<td class="tab-D">The Little Dog</td>
<td class="tab-D">CMI</td>
</tr>
<tr>
<td class="tab-D">Capricornus</td>
<td class="tab-D">The Goat</td>
<td class="tab-D">CAP</td>
</tr>
<tr>
<td class="tab-D">Carina</td>
<td class="tab-D">The Keel</td>
<td class="tab-D">CAR</td>
</tr>
<tr>
<td class="tab-D">Cassiopeia</td>
<td class="tab-D">Cassiopeia</td>
<td class="tab-D">CAS</td>
</tr>
<tr>
<td class="tab-D">Centaurus</td>
<td class="tab-D">The Centaur</td>
<td class="tab-D">CEN</td>
</tr>
<tr>
<td class="tab-D">Cepheus</td>
<td class="tab-D">Cepheus</td>
<td class="tab-D">CEP</td>
</tr>
<tr>
<td class="tab-D">Cetus</td>
<td class="tab-D">The Whale</td>
<td class="tab-D">CET</td>
</tr>
<tr>
<td class="tab-D">Chamaeleon</td>
<td class="tab-D">The Chameleon</td>
<td class="tab-D">CHA</td>
</tr>
<tr>
<td class="tab-D">Circinus</td>
<td class="tab-D">The Pair of Compasses</td>
<td class="tab-D">CIR</td>
</tr>
<tr>
<td class="tab-D">Columba</td>
<td class="tab-D">The Dove</td>
<td class="tab-D">COL</td>
</tr>
<tr>
<td class="tab-D">Coma Berenices</td>
<td class="tab-D">Berenice’s Hair</td>
<td class="tab-D">COM</td>
</tr>
<tr>
<td class="tab-D">Corona Australis</td>
<td class="tab-D">The Southern Crown</td>
<td class="tab-D">CRA</td>
</tr>
<tr>
<td class="tab-D">Corona Borealis</td>
<td class="tab-D">The Northern Crown</td>
<td class="tab-D">CRB</td>
</tr>
<tr>
<td class="tab-D">Corvus</td>
<td class="tab-D">The Crow</td>
<td class="tab-D">CRV</td>
</tr>
<tr>
<td class="tab-D">Crater</td>
<td class="tab-D">The Cup</td>
<td class="tab-D">CRT</td>
</tr>
<tr>
<td class="tab-D">Crux</td>
<td class="tab-D">The Cross</td>
<td class="tab-D">CRU</td>
</tr>
<tr>
<td class="tab-D">Cygnus</td>
<td class="tab-D">The Swan</td>
<td class="tab-D">CYG</td>
</tr>
<tr>
<td class="tab-D">Delphinus</td>
<td class="tab-D">The Dolphin</td>
<td class="tab-D">DEL</td>
</tr>
<tr>
<td class="tab-D">Dorado</td>
<td class="tab-D">The Goldfish</td>
<td class="tab-D">DOR</td>
</tr>
<tr>
<td class="tab-D">Draco</td>
<td class="tab-D">The Dragon</td>
<td class="tab-D">DRA</td>
</tr>
<tr>
<td class="tab-D">Equuleus</td>
<td class="tab-D">The Foal</td>
<td class="tab-D">EQU</td>
</tr>
<tr>
<td class="tab-D">Eridanus</td>
<td class="tab-D">The River</td>
<td class="tab-D">ERI</td>
</tr>
<tr>
<td class="tab-D">Fornax</td>
<td class="tab-D">The Furnace</td>
<td class="tab-D">FOR</td>
</tr>
<tr>
<td class="tab-D">Gemini</td>
<td class="tab-D">The Twins</td>
<td class="tab-D">GEM</td>
</tr>
<tr>
<td class="tab-D">Grus</td>
<td class="tab-D">The Crane</td>
<td class="tab-D">GRU</td>
</tr>
<tr>
<td class="tab-D">Hercules</td>
<td class="tab-D">Hercules</td>
<td class="tab-D">HER</td>
</tr>
<tr>
<td class="tab-D">Horologium</td>
<td class="tab-D">The Pendulum Clock</td>
<td class="tab-D">HOR</td>
</tr>
<tr>
<td class="tab-D">Hydra</td>
<td class="tab-D">The Water Snake</td>
<td class="tab-D">HYA</td>
</tr>
<tr>
<td class="tab-D">Hydrus</td>
<td class="tab-D">The Lesser Water Snake</td>
<td class="tab-D">HYI</td>
</tr>
<tr>
<td class="tab-D">Indus</td>
<td class="tab-D">The Indian</td>
<td class="tab-D">IND</td>
</tr>
<tr>
<td class="tab-D">Lacerta</td>
<td class="tab-D">The Lizard</td>
<td class="tab-D">LAC</td>
</tr>
<tr>
<td class="tab-D">Leo</td>
<td class="tab-D">The Lion</td>
<td class="tab-D">LEO</td>
</tr>
<tr>
<td class="tab-D">Leo Minor</td>
<td class="tab-D">The Lesser Lion</td>
<td class="tab-D">LMI</td>
</tr>
<tr>
<td class="tab-D">Lepus</td>
<td class="tab-D">The Hare</td>
<td class="tab-D">LEP</td>
</tr>
<tr>
<td class="tab-D">Libra</td>
<td class="tab-D">The Scales</td>
<td class="tab-D">LIB</td>
</tr>
<tr>
<td class="tab-D">Lupus</td>
<td class="tab-D">The Wolf</td>
<td class="tab-D">LUP</td>
</tr>
<tr>
<td class="tab-D">Lynx</td>
<td class="tab-D">The Lynx</td>
<td class="tab-D">LYN</td>
</tr>
<tr>
<td class="tab-D">Lyra</td>
<td class="tab-D">The Lyre</td>
<td class="tab-D">LYR</td>
</tr>
<tr>
<td class="tab-D">Mensa</td>
<td class="tab-D">The Table Mountain</td>
<td class="tab-D">MEN</td>
</tr>
<tr>
<td class="tab-D">Microscopium</td>
<td class="tab-D">The Microscope</td>
<td class="tab-D">MIC</td>
</tr>
<tr>
<td class="tab-D">Monoceros</td>
<td class="tab-D">The Unicorn</td>
<td class="tab-D">MON</td>
</tr>
<tr>
<td class="tab-D">Musca</td>
<td class="tab-D">The Fly</td>
<td class="tab-D">MUS</td>
</tr>
<tr>
<td class="tab-D">Norma</td>
<td class="tab-D">The Level</td>
<td class="tab-D">NOR</td>
</tr>
<tr>
<td class="tab-D">Octans</td>
<td class="tab-D">The Octant</td>
<td class="tab-D">OCT</td>
</tr>
<tr>
<td class="tab-D">Ophiuchus</td>
<td class="tab-D">The Serpent Bearer</td>
<td class="tab-D">OPH</td>
</tr>
<tr>
<td class="tab-D">Orion</td>
<td class="tab-D">Orion</td>
<td class="tab-D">ORI</td>
</tr>
<tr>
<td class="tab-D">Pavo</td>
<td class="tab-D">The Peacock</td>
<td class="tab-D">PAV</td>
</tr>
<tr>
<td class="tab-D">Pegasus</td>
<td class="tab-D">Pegasus</td>
<td class="tab-D">PEG</td>
</tr>
<tr>
<td class="tab-D">Perseus</td>
<td class="tab-D">Perseus</td>
<td class="tab-D">PER</td>
</tr>
<tr>
<td class="tab-D"><a id="page24">Phoenix</a></td>
<td class="tab-D">The Phoenix</td>
<td class="tab-D">PHE</td>
</tr>
<tr>
<td class="tab-D">Pictor</td>
<td class="tab-D">The Painter’s Easel</td>
<td class="tab-D">PIC</td>
</tr>
<tr>
<td class="tab-D">Pisces</td>
<td class="tab-D">The Fish</td>
<td class="tab-D">PSC</td>
</tr>
<tr>
<td class="tab-D">Piscis Austrinus</td>
<td class="tab-D">The Southern Fish</td>
<td class="tab-D">PSA</td>
</tr>
<tr>
<td class="tab-D">Puppis</td>
<td class="tab-D">The Stern</td>
<td class="tab-D">PUP</td>
</tr>
<tr>
<td class="tab-D">Pyxis</td>
<td class="tab-D">The Mariner’s Compass</td>
<td class="tab-D">PYX</td>
</tr>
<tr>
<td class="tab-D">Reticulum</td>
<td class="tab-D">The Net</td>
<td class="tab-D">RET</td>
</tr>
<tr>
<td class="tab-D">Sagitta</td>
<td class="tab-D">The Arrow</td>
<td class="tab-D">SGE</td>
</tr>
<tr>
<td class="tab-D">Sagittarius</td>
<td class="tab-D">The Archer</td>
<td class="tab-D">SGR</td>
</tr>
<tr>
<td class="tab-D">Scorpius</td>
<td class="tab-D">The Scorpion</td>
<td class="tab-D">SCO</td>
</tr>
<tr>
<td class="tab-D">Sculptor</td>
<td class="tab-D">The Sculptor</td>
<td class="tab-D">SCL</td>
</tr>
<tr>
<td class="tab-D">Scutum</td>
<td class="tab-D">The Shield</td>
<td class="tab-D">SCT</td>
</tr>
<tr>
<td class="tab-D">Serpens Caput and Cauda</td>
<td class="tab-D">The Serpent</td>
<td class="tab-D">SER</td>
</tr>
<tr>
<td class="tab-D">Sextans</td>
<td class="tab-D">The Sextant</td>
<td class="tab-D">SEX</td>
</tr>
<tr>
<td class="tab-D">Taurus</td>
<td class="tab-D">The Bull</td>
<td class="tab-D">TAU</td>
</tr>
<tr>
<td class="tab-D">Telescopium</td>
<td class="tab-D">The Telescope</td>
<td class="tab-D">TEL</td>
</tr>
<tr>
<td class="tab-D">Triangulum</td>
<td class="tab-D">The Triangle</td>
<td class="tab-D">TRI</td>
</tr>
<tr>
<td class="tab-D">Triangulum Australe</td>
<td class="tab-D">The Southern Triangle</td>
<td class="tab-D">TRA</td>
</tr>
<tr>
<td class="tab-D">Tucana</td>
<td class="tab-D">The Toucan</td>
<td class="tab-D">TUC</td>
</tr>
<tr>
<td class="tab-D">Ursa Major</td>
<td class="tab-D">The Great Bear</td>
<td class="tab-D">UMA</td>
</tr>
<tr>
<td class="tab-D">Ursa Minor</td>
<td class="tab-D">The Little Bear</td>
<td class="tab-D">UMI</td>
</tr>
<tr>
<td class="tab-D">Vela</td>
<td class="tab-D">The Sail</td>
<td class="tab-D">VEL</td>
</tr>
<tr>
<td class="tab-D">Virgo</td>
<td class="tab-D">The Virgin</td>
<td class="tab-D">VIR</td>
</tr>
<tr>
<td class="tab-D">Volans</td>
<td class="tab-D">The Flying Fish</td>
<td class="tab-D">VOL</td>
</tr>
<tr>
<td class="tab-D">Vulpecula</td>
<td class="tab-D">The Fox</td>
<td class="tab-D">VUL</td>
</tr>
</tbody></table>
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Guide.vue?056145ec